import type {SiteLocale} from "seo/siteMeta";
import {localeCopyWithEnglishFallback} from "i18n/localeFallback";

/**
 * Per-page copy for the public **Contact & Support** page (`/contact`, issue ui#133).
 *
 * This module is one of the TWO files the content lives in (the other is {@link ../ContactContent.tsx}).
 * It holds every locale - English (transferify.eu), Romanian (transferify.ro), German
 * (transferify.de), French (transferify.eu/fr) and Italian (transferify.eu/it) - and the page picks one at
 * runtime via `useLocale()`, exactly like the rest of the site. Nothing else needs to
 * change: routes, SEO wiring and the Resources link already exist.
 *
 * v1 is deliberately **email-only, no contact form** (a form is deferred to a follow-up issue). The
 * support ADDRESS is not copy and is not here: it is the storefront's own inbox, which
 * `support/supportAddress.ts` derives from the page's storefront subfolder, the host and the brand registry
 * (kitecorp/transferify-ui#2264, kitecorp/transferify-ui#2833), so the same English sentence names
 * transferify.eu's own inbox on transferify.eu and transferify.ro's own inbox on transferify.ro.
 *
 * The component is a dumb view: it only lays these strings out and wires the two links (a `mailto:`
 * for the address, `react-router` links for the internal FAQ/pricing nudges). To reword the page,
 * edit the strings here; to restructure it, edit the component.
 */
export interface ContactCopy {
	/** `<Seo>` metadata - the browser/search title and meta description for this page. */
	seo: {
		/** Page title. `<Seo>` appends ` | Transferify`, so do NOT repeat the brand here. */
		title: string;
		/** Meta description (~150-160 chars for a healthy search snippet). */
		description: string;
	};
	/** The single `<h1>` at the top of the page. */
	heading: string;
	/** One-line intro paragraph directly under the heading (warm, human, sets the tone). */
	lede: string;

	/** `<h2>` above the email block. */
	emailHeading: string;
	/**
	 * The sentence that wraps the email link. `before` is rendered, then the address as a link, then
	 * `after` - so keep the trailing/leading spacing (`before` ends with a space, `after` opens with a
	 * space + dash) exactly as written.
	 */
	emailLine: {before: string; after: string};
	/** Soft response-time expectation - a friendly guide, not a hard SLA. */
	responseTime: string;

	/** `<h2>` above the "this might be faster" self-serve nudge. */
	fasterHeading: string;
	/** Lead-in sentence before the two internal links. */
	fasterIntro: string;
	/** FAQ link: the linked `label`, then `description` rendered right after it (opens with ` - `). */
	faqLink: {label: string; description: string};
	/** Pricing link: the linked `label`, then `description` rendered right after it (opens with ` - `). */
	pricingLink: {label: string; description: string};

	/** Closing reassurance line - nudges the reader to just write to us. */
	closing: string;
}

/** EN + RO + DE + FR + IT copy, keyed by locale. `ContactContent` reads `contactCopy[locale]`. */
export const contactCopy: Record<SiteLocale, ContactCopy> = localeCopyWithEnglishFallback<ContactCopy>({
	en: {
		seo: {
			title: "Contact & Support",
			description:
				"Need a hand with Transferify? Email our support team and a real person will get back to " +
				"you, usually within 1-2 business days.",
		},
		heading: "Contact & Support",
		lede:
			"Have a question, hit a snag, or just want to talk to a human? We're a small team, and a " +
			"real person reads every message that reaches us.",

		emailHeading: "Email us",
		emailLine: {
			before: "The best way to reach us is by email at ",
			after:
				" - tell us what you were trying to do and what happened, and a screenshot or the transfer " +
				"link always helps us get to the bottom of it faster.",
		},
		responseTime:
			"We usually reply within 1-2 business days. If something is urgent - a transfer that won't " +
			"download, or a billing question - say so in the subject line and we'll do our best to get " +
			"to you sooner.",

		fasterHeading: "Before you write, this might be faster",
		fasterIntro:
			"A lot of questions already have an answer waiting. Two quick places worth checking first:",
		faqLink: {
			label: "Frequently asked questions",
			description:
				" - how transfers, encryption, links and expiry work, explained in plain language.",
		},
		pricingLink: {
			label: "Pricing & plans",
			description:
				" - what's included for free, the size and storage limits, and what Premium adds on top.",
		},
		closing:
			"Still stuck? Don't overthink it - send us that email. We'd much rather hear from you than " +
			"have you guessing.",
	},
	ro: {
		seo: {
			title: "Contact și asistență",
			description:
				"Ai nevoie de ajutor cu Transferify? Scrie-ne echipei de asistență și un om real îți " +
				"răspunde, de obicei în 1-2 zile lucrătoare.",
		},
		heading: "Contact și asistență",
		lede:
			"Ai o întrebare, te-ai împotmolit undeva sau vrei pur și simplu să vorbești cu un om? Suntem " +
			"o echipă mică, iar fiecare mesaj care ajunge la noi este citit de o persoană reală.",

		emailHeading: "Scrie-ne pe e-mail",
		emailLine: {
			before: "Cel mai simplu ne găsești pe e-mail, la adresa ",
			after:
				" - spune-ne ce încercai să faci și ce s-a întâmplat, iar o captură de ecran sau linkul " +
				"transferului ne ajută mereu să găsim soluția mai repede.",
		},
		responseTime:
			"De obicei răspundem în 1-2 zile lucrătoare. Dacă e ceva urgent - un transfer care nu se " +
			"descarcă sau o întrebare legată de facturare - menționează asta în subiect și încercăm să " +
			"revenim mai devreme.",

		fasterHeading: "Înainte să ne scrii, s-ar putea să fie mai rapid așa",
		fasterIntro:
			"Multe întrebări au deja un răspuns pregătit. Două locuri unde merită să te uiți întâi:",
		faqLink: {
			label: "Întrebări frecvente",
			description:
				" - cum funcționează transferurile, criptarea, linkurile și expirarea, explicate pe " +
				"înțelesul tuturor.",
		},
		pricingLink: {
			label: "Prețuri și planuri",
			description:
				" - ce este inclus gratuit, limitele de dimensiune și de stocare și ce aduce în plus Premium.",
		},
		closing:
			"Tot blocat? Nu complica lucrurile - trimite-ne acel e-mail. Preferăm să auzim de la tine " +
			"decât să rămâi cu semne de întrebare.",
	},
	de: {
		seo: {
			title: "Kontakt und Support",
			description:
				"Brauchst du Hilfe mit Transferify? Schreib unserem Support-Team, und ein echter Mensch " +
				"meldet sich, meist innerhalb von 1-2 Werktagen.",
		},
		heading: "Kontakt und Support",
		lede:
			"Hast du eine Frage, hängst du irgendwo fest oder willst du einfach mit einem Menschen " +
			"sprechen? Wir sind ein kleines Team, und jede Nachricht, die uns erreicht, liest eine echte Person.",

		emailHeading: "Schreib uns eine E-Mail",
		emailLine: {
			before: "Am einfachsten erreichst du uns per E-Mail unter ",
			after:
				" - sag uns, was du tun wolltest und was stattdessen passiert ist; ein Screenshot oder der " +
				"Transfer-Link hilft uns immer, der Sache schneller auf den Grund zu gehen.",
		},
		responseTime:
			"Normalerweise antworten wir innerhalb von 1-2 Werktagen. Wenn etwas dringend ist - ein " +
			"Transfer, der nicht herunterlädt, oder eine Frage zur Abrechnung - schreib das in den Betreff, " +
			"und wir geben unser Bestes, früher bei dir zu sein.",

		fasterHeading: "Bevor du schreibst: Das geht vielleicht schneller",
		fasterIntro:
			"Auf viele Fragen wartet schon eine Antwort. Zwei Stellen, an denen sich ein Blick zuerst lohnt:",
		faqLink: {
			label: "Häufige Fragen",
			description:
				" - wie Transfers, Verschlüsselung, Links und Ablauf funktionieren, in klarer Sprache erklärt.",
		},
		pricingLink: {
			label: "Preise und Pläne",
			description:
				" - was kostenlos enthalten ist, die Grenzen für Größe und Speicher, und was Premium obendrauf legt.",
		},
		closing:
			"Immer noch festgefahren? Denk nicht zu lange darüber nach - schick uns diese E-Mail. Wir hören " +
			"lieber von dir, als dass du rätst.",
	},
	fr: {
		seo: {
			title: "Contact et assistance",
			description:
				"Besoin d’aide avec Transferify\u00a0? Écrivez à notre équipe d’assistance\u00a0: une vraie personne vous " +
				"répond, en général sous 1 à 2 jours ouvrés.",
		},
		heading: "Contact et assistance",
		lede:
			"Une question, un souci, ou l’envie de parler à un humain\u00a0? Nous sommes une petite équipe, et " +
			"chaque message qui nous parvient est lu par une vraie personne.",

		emailHeading: "Écrivez-nous par e-mail",
		emailLine: {
			before: "Le plus simple pour nous joindre est l’e-mail, à l’adresse ",
			after:
				". Dites-nous ce que vous essayiez de faire et ce qui s’est passé\u00a0: une capture d’écran ou le lien " +
				"du transfert nous aide toujours à comprendre plus vite.",
		},
		responseTime:
			"Nous répondons en général sous 1 à 2 jours ouvrés. Si c’est urgent (un transfert qui ne se " +
			"télécharge pas, une question de facturation), dites-le dans l’objet et nous ferons de notre mieux " +
			"pour vous répondre plus tôt.",

		fasterHeading: "Avant de nous écrire, c’est peut-être plus rapide",
		fasterIntro:
			"Beaucoup de questions ont déjà une réponse toute prête. Deux endroits à consulter d’abord\u00a0:",
		faqLink: {
			label: "Questions fréquentes",
			description:
				"\u00a0: comment fonctionnent les transferts, le chiffrement, les liens et l’expiration, expliqués simplement.",
		},
		pricingLink: {
			label: "Tarifs et plans",
			description:
				"\u00a0: ce qui est inclus gratuitement, les limites de taille et de stockage, et ce que Premium ajoute en plus.",
		},
		closing:
			"Toujours bloqué\u00a0? Ne réfléchissez pas trop\u00a0: envoyez-nous cet e-mail. Nous préférons largement avoir " +
			"de vos nouvelles que vous savoir dans le doute.",
	},
	it: {
		seo: {
			title: "Contatti e assistenza",
			description:
				"Serve aiuto con Transferify? Scriva al nostro team di assistenza: risponde una persona vera, " +
				"di solito entro 1-2 giorni lavorativi.",
		},
		heading: "Contatti e assistenza",
		lede:
			"Ha una domanda, ha trovato un intoppo o vuole semplicemente parlare con una persona? Siamo un " +
			"piccolo team e ogni messaggio che ci arriva viene letto da una persona vera.",

		emailHeading: "Ci scriva via e-mail",
		emailLine: {
			before: "Il modo migliore per raggiungerci è l’e-mail, all’indirizzo ",
			after:
				". Ci racconti che cosa stava cercando di fare e che cosa è successo: una schermata o il link " +
				"del trasferimento ci aiuta sempre a capire più in fretta.",
		},
		responseTime:
			"Di solito rispondiamo entro 1-2 giorni lavorativi. Se la questione è urgente (un trasferimento " +
			"che non si scarica, una domanda sulla fatturazione), lo indichi nell’oggetto e faremo il possibile " +
			"per rispondere prima.",

		fasterHeading: "Prima di scriverci, forse c’è una via più rapida",
		fasterIntro:
			"Molte domande hanno già una risposta pronta. Due posti da consultare per primi:",
		faqLink: {
			label: "Domande frequenti",
			description:
				": come funzionano trasferimenti, crittografia, link e scadenza, spiegati in modo semplice.",
		},
		pricingLink: {
			label: "Prezzi e piani",
			description:
				": che cosa è incluso gratuitamente, i limiti di dimensione e di archiviazione e che cosa aggiunge Premium.",
		},
		closing:
			"Ancora in difficoltà? Non ci pensi troppo: ci scriva pure. Preferiamo di gran lunga ricevere un " +
			"messaggio che lasciare la questione irrisolta.",
	},
});
